import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import { fetchTrackingPayments, type TrackingPayment } from "../services/tracking/paymentsService";
import {
  fetchParentInvoices,
  payInvoice as payInvoiceApi,
  bulkPayInvoices as bulkPayInvoicesApi,
  type Invoice,
  type InvoiceListResponse,
  type BulkPayPayload,
  type InvoicePaymentCreatePayload,
} from "../services/tracking/invoicesService";

const PAYMENTS_KEY = "tracking-payments";
const INVOICES_KEY = "tracking-parent-invoices";

export function useTrackingPayments() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<TrackingPayment[]>(
    token ? [PAYMENTS_KEY, token] : null,
    () => fetchTrackingPayments(token),
    { revalidateOnFocus: false }
  );

  const list = Array.isArray(data) ? data : [];
  return {
    payments: list,
    isLoading,
    error,
    refetch: mutate,
    accessToken: token,
  };
}

/** Alias pour compatibilité avec les pages parent (paiements) */
export function useParentPayments() {
  return useTrackingPayments();
}

export function useParentInvoices(params?: { status?: string; domain?: string; page?: number; page_size?: number }) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<InvoiceListResponse>(
    token ? [INVOICES_KEY, token, params?.status ?? "", params?.domain ?? "", params?.page ?? 1, params?.page_size ?? 50] : null,
    () => fetchParentInvoices(token, params),
    { revalidateOnFocus: false }
  );

  const list = Array.isArray(data?.results) ? data!.results : [];

  return {
    invoices: list,
    isLoading,
    error,
    refetch: mutate,
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    payInvoice: (invoiceId: string, payload: InvoicePaymentCreatePayload) => payInvoiceApi(token, invoiceId, payload),
    bulkPayInvoices: (payload: BulkPayPayload) => bulkPayInvoicesApi(token, payload),
  };
}

export type { TrackingPayment, Invoice };
